import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  Select,
  Paper,
  FormControl,
  InputLabel,
} from '@mui/material';
import { 
  CloudUpload,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import axiosInstance from '../../../utils/axios';
import { toLower } from 'lodash';


const DropdownOptionsModal = ({ open, onClose, onSave, initialOptions = [] }) => {
  const [options, setOptions] = useState(['']);

  useEffect(() => {
    if (open) {
      setOptions(initialOptions.length > 0 ? [...initialOptions] : ['']);
    }
  }, [open, initialOptions]);

  const handleAddOption = () => {
    setOptions(prev => [...prev, '']);
  };

  const handleRemoveOption = (index) => {
    setOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index, value) => {
    setOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleSave = () => {
    const validOptions = options.filter(option => option.trim() !== '');
    onSave(validOptions);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Manage Dropdown Options</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {options.map((option, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label={`Option ${index + 1}`}
                value={option}
                onChange={(e) => handleOptionChange(index, e.target.value)}
                fullWidth
                size="small"
              />
              {options.length > 1 && (
                <IconButton 
                  onClick={() => handleRemoveOption(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          
          <Button 
            variant="outlined" 
            onClick={handleAddOption}
            startIcon={<AddIcon />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Option
          </Button>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save Options</Button>
      </DialogActions>
    </Dialog>
  );
};

const SetupDeliveryDialog = ({ open, onClose, onConfirm, newColumns, defaultFormValues, customFields, aliase, handleFetch, setFields }) => {
  const [list, setList] = useState([]);
  const [dropdownModalOpen, setDropdownModalOpen] = useState(false);
  const [currentDropdownIndex, setCurrentDropdownIndex] = useState(null);
  const [loader, setLoader] = useState(false);

  const processCustomFieldValues = (values, type) => {
    if (type === 3) { 
      if (Array.isArray(values)) {
        return values;
      } else if (typeof values === 'string' && values.trim()) {
        return values.split(',').map(v => v.trim()).filter(v => v);
      }
      return [];
    }
    return Array.isArray(values) ? values.join(', ') : (values || '');
  };

  useEffect(() => {
    if (open) {
      let initialList = [];

      if (defaultFormValues?.length) {
        const defaultItems = defaultFormValues.map((item, i) => ({
          ...item,
          key: item.key ?? `default_${i}`,
          valueType: item.valueType ?? item.value ?? 2,
          value: item.actualValue ?? '',
          dropdownOptions: item.dropdownOptions || [],
          isCustomField: false,
          isNameOrAlias: item.key === 'name' || item.key === aliase || 
                        item.label?.toLowerCase().includes('name') || 
                        item.label?.toLowerCase().includes(toLower(aliase)),
          isExistingCustomField: false,
          label: (item?.label === "Name" && aliase)  ? aliase : item?.label
        }));
        initialList = [...initialList, ...defaultItems];
      }

      if (customFields?.length) {
        const customItems = customFields.map((field) => ({
          key: `custom_${field.id}`,
          label: field.name,
          valueType: field.type,
          value: field.type === 3 ? '' : processCustomFieldValues(field.values, field.type),
          dropdownOptions: field.type === 3 ? processCustomFieldValues(field.values, field.type) : [],
          isCustomField: true,
          isNameOrAlias: false,
          isExistingCustomField: true,
          customFieldId: field.id,
          projectId: field.project_id,
          id: field?.id,
        }));
        initialList = [...initialList, ...customItems];
      }
      setList(initialList);
    }
    // eslint-disable-next-line
  }, [open, defaultFormValues, customFields]);

  const handleChange = (index, field, value) => {
    setList((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      if (field === 'valueType' && value === 3) {
        if (!updated[index].dropdownOptions || updated[index].dropdownOptions.length === 0) {
          updated[index].dropdownOptions = [];
        }
        setTimeout(() => {
          setCurrentDropdownIndex(index);
          setDropdownModalOpen(true);
        }, 100);
      }
      
      return updated;
    });
  };

  const handleAddCustomField = () => {
    setList((prev) => [
      ...prev,
      {
        key: `new_${Date.now()}`,
        label: '',
        valueType: 2, 
        value: '',
        dropdownOptions: [],
        isCustomField: true,
        isNameOrAlias: false,
        isExistingCustomField: false
      }
    ]);
  };

  const handleRemoveCustomField = (index) => {
    setList((prev) => {
      const updated = [...prev];
      const item = updated[index];
      
      if (item.isExistingCustomField) {
        updated[index] = { ...updated[index], deleted: true };
      } else {
        return prev.filter((_, i) => i !== index);
      }
      
      return updated;
    });
  };

  const handleDropdownOptionsChange = (index) => {
    setCurrentDropdownIndex(index);
    setDropdownModalOpen(true);
  };

  const handleSaveDropdownOptions = (options) => {
    if (currentDropdownIndex !== null) {
      setList((prev) => {
        const updated = [...prev];
        updated[currentDropdownIndex] = { 
          ...updated[currentDropdownIndex], 
          dropdownOptions: options 
        };
        return updated;
      });
    }
    setCurrentDropdownIndex(null);
  };

  const updateCustomFields = async (title, value) => {
    setLoader(true)
    try {
        const res = await axiosInstance.post(`projects/delivery/custom_inputs?form_name=delivery&field_name=delivery_reference&field_alias_name=${title}`, value)
        if(res.data) {
          await setFields()
          handleFetch()
          onClose()
        }
    } catch(err) {
      console.error(err)
    } finally {
      setLoader(false)
    }
  }

  const handleSave = () => {
    
    const result = list.map(item => ({
      name: item.label,
      type: item.valueType,
      values: item.dropdownOptions?.length ? item.dropdownOptions : '',
      ...(item?.id && { id: item?.id }),
      ...(item.deleted && { delete: item.deleted }) 
    }));

    updateCustomFields(result?.[2]?.name, result?.slice(5));
  };

  const visibleList = list.filter(item => !item.deleted);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Typography variant="h6">Setup Delivery</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box px={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {visibleList.map(({ label, key, valueType, value, dropdownOptions, isCustomField, isNameOrAlias }, index) => {
              const originalIndex = list.findIndex(item => item.key === key);
              
              const isFieldEditable = isCustomField || isNameOrAlias;
              const isDisabled = !isFieldEditable;

              const inputPropsCommon = {
                disabled: isDisabled,
                fullWidth: true,
                size: 'small',
                sx: {
                  fontSize: '16px',
                  backgroundColor: isDisabled ? '#f5f5f5' : 'white'
                },
                inputProps: { style: { fontSize: '16px' } }
              };

              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    width: '100%',
                    p: 2,
                    border:'none',
                    borderRadius: 0,
                    backgroundColor: 'transparent'
                  }}
                >
                  <Box sx={{ flex: '0 0 40%' }}>
                    {isCustomField || isNameOrAlias ? (
                      <TextField
                        placeholder={isCustomField ? "Enter field name" : "Enter name/alias"}
                        size="small"
                        fullWidth
                        value={label}
                        onChange={(e) => handleChange(originalIndex, 'label', e.target.value)}
                        label={isCustomField ? "Field Name" : "Delivery Reference"}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{label}</Typography>
                    )}
                  </Box>

                  <Box sx={{ flex: '0 0 50%' }}>
                    {isCustomField ? (
                      <FormControl fullWidth size="small">
                        <InputLabel>Field Type</InputLabel>
                        <Select
                          value={valueType}
                          onChange={(e) => handleChange(originalIndex, 'valueType', e.target.value)}
                          label="Field Type"
                        >
                          <MenuItem value={0}>Number</MenuItem>
                          <MenuItem value={1}>Decimal</MenuItem>
                          <MenuItem value={2}>Text</MenuItem>
                          <MenuItem value={3}>Dropdown</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      (() => {
                        switch (valueType) {
                          case 0:
                          case 1:
                            return (
                              <TextField
                                type="number"
                                label={`Enter ${label}`}
                                {...inputPropsCommon}
                                value={value}
                                onChange={(e) => handleChange(originalIndex, 'value', e.target.value)}
                              />
                            );
                          case 2:
                            return (
                              <TextField
                                label={`Enter ${label}`}
                                {...inputPropsCommon}
                                value={value}
                                onChange={(e) => handleChange(originalIndex, 'value', e.target.value)}
                                disabled={!isCustomField}
                                 sx={{ backgroundColor: !isCustomField ? '#f5f5f5' : 'white' }}
                              />
                            );
                          case 3:
                            return (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <TextField
                                  select
                                  {...inputPropsCommon}
                                  value={value}
                                  onChange={(e) => handleChange(originalIndex, 'value', e.target.value)}
                                  sx={{ flex: 1 }}
                                >
                                  {dropdownOptions.map((option, optIndex) => (
                                    <MenuItem key={optIndex} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                {!isDisabled && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleDropdownOptionsChange(originalIndex)}
                                  >
                                    Options
                                  </Button>
                                )}
                              </Box>
                            );
                          case 4:
                            return (
                              <DatePicker
                                value={value || null}
                                onChange={(newValue) => handleChange(originalIndex, 'value', newValue)}
                                disabled={isDisabled}
                                slotProps={{
                                  textField: {
                                    ...inputPropsCommon
                                  }
                                }}
                              />
                            );
                          case 5:
                            return (
                              <TimePicker
                                value={value || null}
                                onChange={(newValue) => handleChange(originalIndex, 'value', newValue)}
                                disabled={isDisabled}
                                slotProps={{
                                  textField: {
                                    ...inputPropsCommon
                                  }
                                }}
                              />
                            );
                          case 6:
                            return (
                              <TextField
                                label={`Enter ${label}`}
                                multiline
                                minRows={2}
                                {...inputPropsCommon}
                                value={value}
                                onChange={(e) => handleChange(originalIndex, 'value', e.target.value)}
                              />
                            );
                          case 7:
                            return (
                              <Paper
                                variant="outlined"
                                sx={{
                                  borderStyle: "dashed",
                                  borderWidth: 2,
                                  borderColor: 'grey.300',
                                  padding: 1,
                                  textAlign: "center",
                                  cursor: "not-allowed",
                                  bgcolor: '#f5f5f5',
                                  borderRadius: "5px",
                                  transition: 'all 0.2s ease-in-out',
                                  '&:hover': {
                                    bgcolor: 'action.hover',
                                  }
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                  <CloudUpload sx={{ fontSize: 24, color: 'grey.400' }} />
                                  <Typography variant="h6" fontSize={16} color="textPrimary" fontWeight={500}>
                                    Click or Drag Files Here
                                  </Typography>
                                  <Typography variant="body2" fontSize={12} color="textSecondary">
                                    JPG, PNG, PDF (Max 10MB)
                                  </Typography>
                                </Box>
                              </Paper>
                            );
                          default:
                            return null;
                        }
                      })()
                    )}
                  </Box>

                  <Box sx={{ flex: '0 0 2%', display: 'flex', justifyContent: 'flex-end' }}>
                  {valueType === 3 ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDropdownOptionsChange(originalIndex)}
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                    ) : <Button sx={{ mr: 1 }}></Button>}
                    </Box>

                  <Box sx={{ flex: '0 0 2%', display: 'flex', justifyContent: 'flex-end' }}>
                   
                    {isCustomField && (
                      <IconButton 
                        onClick={() => handleRemoveCustomField(originalIndex)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              );
            })}

            <Box sx={{ mt: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleAddCustomField}
                startIcon={<AddIcon />}
                sx={{ 
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  width: '100%'
                }}
              >
                Add Custom Field
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Box p={2} sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleSave} loading={loader} variant="contained" color="primary">
              Save 
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Dropdown Options Modal */}
      <DropdownOptionsModal
        open={dropdownModalOpen}
        onClose={() => {
          setDropdownModalOpen(false);
          setCurrentDropdownIndex(null);
        }}
        onSave={handleSaveDropdownOptions}
        initialOptions={currentDropdownIndex !== null ? list[currentDropdownIndex]?.dropdownOptions || [] : []}
      />
    </>
  );
};

export default SetupDeliveryDialog;